import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Slider,
  FormHelperText,
} from "@mui/material";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";

import {
  ACCENT,
  ACCENT_TINT,
  ACCENT_TINT_STRONG,
  FONT,
  INK,
  INK_MUTED,
  RADIUS,
  STATUS,
} from "../../../../theme/tokens";
import { SubHeader } from "./WizardScaffold";

// Slider + precise number entry for larger-range resources (memory, vCPUs).
// Token-aligned: slim accent track, value-driven number field, muted min/max.
const InputWithSlider = ({
  id,
  label,
  description,
  value,
  onChange,
  helperText,
  error,
  disabled,
  minInput = 1,
  maxInput,
  step = 1,
}) => {
  const handleSliderChange = (event, newValue) => {
    onChange({ target: { value: newValue } });
  };

  const handleBlur = () => {
    let numValue = Number(value);
    if (isNaN(numValue) || numValue < minInput) numValue = minInput;
    else if (numValue > maxInput) numValue = maxInput;
    onChange({ target: { value: numValue } });
  };

  const sliderValue =
    typeof value === "number"
      ? value
      : isNaN(Number(value))
        ? minInput
        : Number(value);

  // Native browser spin buttons are hidden (see the TextField's sx below) in
  // favor of these two — same look everywhere instead of each browser's own
  // inconsistent, unstyled up/down arrows.
  const handleStep = (delta) => {
    const next = Math.min(maxInput, Math.max(minInput, sliderValue + delta));
    onChange({ target: { value: next } });
  };

  return (
    <Box sx={{ py: 0.5 }}>
      <SubHeader sx={{ mb: 0.75 }}>{label}</SubHeader>
      <Typography
        sx={{
          color: INK_MUTED,
          fontSize: FONT.sm,
          mb: 1.25,
        }}
      >
        {description}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "flex-start" },
          gap: { xs: 1.25, sm: 2 },
        }}
      >
        <Box sx={{ flex: { xs: "1 1 auto", sm: "0 0 40%" }, minWidth: 0 }}>
          <TextField
            id={id}
            type="number"
            value={value}
            onChange={onChange}
            onBlur={handleBlur}
            disabled={disabled}
            error={error}
            fullWidth
            size="small"
            inputProps={{
              "aria-label": label,
              min: minInput,
              max: maxInput,
              step,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    size="small"
                    tabIndex={-1}
                    aria-label={`Decrease ${label}`}
                    disabled={disabled || sliderValue <= minInput}
                    onClick={() => handleStep(-step)}
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: `${RADIUS.sm}px`,
                      color: ACCENT,
                      "&:hover": { backgroundColor: ACCENT_TINT },
                    }}
                  >
                    <RemoveRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    tabIndex={-1}
                    aria-label={`Increase ${label}`}
                    disabled={disabled || sliderValue >= maxInput}
                    onClick={() => handleStep(step)}
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: `${RADIUS.sm}px`,
                      color: ACCENT,
                      "&:hover": { backgroundColor: ACCENT_TINT },
                    }}
                  >
                    <AddRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: INK,
                minHeight: 48,
                pl: 0.5,
                pr: 0.5,
              },
              "& .MuiOutlinedInput-input": {
                py: 1.5,
                textAlign: "center",
              },
              // Hide the native number-input spin buttons — every browser
              // renders its own unstyled version that clashes with the
              // adornment buttons above, which replace them.
              "& input[type=number]": {
                MozAppearance: "textfield",
              },
              "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                {
                  WebkitAppearance: "none",
                  margin: 0,
                },
            }}
          />

          <FormHelperText
            error={error}
            sx={{ minHeight: "1.66em", ml: 1.75, mt: 0.5 }}
          >
            {helperText}
          </FormHelperText>
        </Box>

        <Box
          sx={{
            flex: { xs: "1 1 auto", sm: "1 1 60%" },
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 2,
            minHeight: 48,
          }}
        >
          <LimitPill value={minInput} />
          <Slider
            value={sliderValue}
            onChange={handleSliderChange}
            aria-label={label}
            min={minInput}
            max={maxInput}
            step={step}
            disabled={disabled}
            sx={{
              flex: 1,
              minWidth: 72,
              height: 4,
              color: ACCENT,
              "& .MuiSlider-rail": {
                opacity: 1,
                backgroundColor: ACCENT_TINT_STRONG,
              },
              "& .MuiSlider-track": {
                backgroundColor: ACCENT,
                border: 0,
              },
              "& .MuiSlider-thumb": {
                backgroundColor: ACCENT,
                width: 16,
                height: 16,
                boxShadow: "0 1px 4px rgba(15,23,42,0.2)",
                "&:hover, &.Mui-focusVisible": {
                  boxShadow: "0 0 0 4px rgba(63,154,130,0.16)",
                },
              },
            }}
          />
          <LimitPill value={maxInput} />
        </Box>
      </Box>
    </Box>
  );
};

function LimitPill({ value }) {
  return (
    <Typography
      component="span"
      sx={{
        minWidth: 64,
        px: 1.75,
        py: 1.25,
        borderRadius: 2,
        bgcolor: "rgba(59,130,246,0.12)",
        color: STATUS.info,
        fontSize: FONT.md,
        lineHeight: 1,
        textAlign: "center",
        fontVariantNumeric: "tabular-nums",
        flexShrink: 0,
      }}
    >
      {value}
    </Typography>
  );
}

LimitPill.propTypes = {
  value: PropTypes.number.isRequired,
};

InputWithSlider.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  helperText: PropTypes.node.isRequired,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  minInput: PropTypes.number,
  maxInput: PropTypes.number.isRequired,
  step: PropTypes.number,
};

export default InputWithSlider;
